<script setup lang="ts">
import { DialogModal } from '@metapic-apps/shared'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { useGetUserTags } from '@/api/user-tags.api'
import ArrowIcon from '@/assets/icons/arrow.svg?component'
import AddUserDialog from '@/components/AddUserDialog/AddUserDialog.vue'
import CampaignFixedFee from '@/components/CampaignParticipants/CampaignFixedFee.vue'
import CampaignParticipantsColumns from '@/components/CampaignParticipants/CampaignParticipantsColumns.vue'
import CampaignParticipantsSettings from '@/components/CampaignParticipants/CampaignParticipantsSettings.vue'
import { useCampaignDefaultPayment } from '@/lib/hooks/campaign-payment.hooks'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { useApp } from '@/stores/app.stores'
import { usePageTitleStore } from '@/stores/page-title.stores'
import type { UserTag } from '@/types/userTags'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()
const appStore = useApp()
const pageTitleStore = usePageTitleStore()

const advertiserStore = useAdvertiserStore()

const advertiser = computed(() => advertiserStore?.advertiser)
const advertiserId = computed(() => advertiser.value?.id)
const userHasPermissionsToEdit = computed(() => !store.getters['shop/hideEdit']) // TODO: legacy way of getting user permissions through store
const campaign = computed(() => store.getters['campaign/campaignInfo']) // TODO: legacy way of getting a campaign through store
const isCampaignLoading = computed(() => store.getters['campaign/isLoading']) // TODO: legacy way of getting campaign loading state through store
const campaignPayment = useCampaignDefaultPayment(campaign)
const campaignLoadError = computed(() => store.getters['campaign/error']) // TODO: legacy way of getting a campaign through store
const { data: userTags } = useGetUserTags(advertiserId)
const onboardingStep = computed(() => store.getters['onboarding/currentOnboardingStep'])

const userTagsFiltered = computed<UserTag[]>(() => userTags.value.filter((tag: UserTag) => !campaign.value?.tagIds?.includes(tag.id)))
const isInviteUsersModalOpen = ref(false)
const searchQuery = ref('')
const isMediaLinkVisible = ref(false)
const shouldUpdateFirstColumn = ref(false)

const onInvite = () => {
  shouldUpdateFirstColumn.value = true
}
const handleCloseSuccessCreationPopup = async () => {
  await router.push(`/campaign-users/${route.params.id}`)
  if (store.getters['onboarding/currentOnboardingStep'] === 2) {
    await store.dispatch('onboarding/setCurrentCard', 'campaign-finish-section')
    await store.dispatch('onboarding/setOnboardingActivity', true)
  }
}
watch(
  () => route.params.id,
  (newId: string | string[]) => appStore.fetchCampaignAdvertiser(newId as string),
)
watch(
  () => campaign.value?.title,
  (title: string | undefined) => pageTitleStore.setTitle(title ?? null),
  { immediate: true },
)
onMounted(async () => {
  if (!isCampaignLoading.value && Number(route.params.id) !== campaign.value?.id) {
    await appStore.fetchCampaignAdvertiser(route.params.id as string)
  }
  if (onboardingStep.value === 3) {
    await store.dispatch('onboarding/setCurrentCard', 'manage-campaign-track-progress-section')
    await store.dispatch('onboarding/setOnboardingActivity', true)
  }
  if (onboardingStep.value === 3) {
    await store.dispatch('onboarding/setCurrentCard', 'manage-campaign-track-progress-section')
    await store.dispatch('onboarding/setOnboardingActivity', true)
  }
})
onUnmounted(() => {
  pageTitleStore.setTitle(null)
  void store.dispatch('campaign/reset')
})
</script>

<template>
  <div>
    <loading-spinner v-if="isCampaignLoading" />
    <template v-else-if="campaignLoadError">
      <BoxComponent :mt="56" :mb="48" :pl="24">
        <BoxComponent :mt="56" :mb="48" :pr="24">
          <TypographyComponent variant="headline-medium" text-align="center">Something went wrong</TypographyComponent>
          <TypographyComponent is="p" variant="body-large" text-align="center">
            You don't have access to this campaign or it does not exist.
          </TypographyComponent>
          <BoxComponent :mt="24">
            <router-link to="/campaigns">
              <FlexComponent justify="center" :gap="8">
                <ArrowIcon class="back-link" />
                <TypographyComponent is="p" variant="body-large" text-align="center"> Go back to campaigns </TypographyComponent>
              </FlexComponent>
            </router-link>
          </BoxComponent>
        </BoxComponent>
      </BoxComponent>
    </template>
    <BoxComponent v-else-if="campaign" :mt="32" :mb="48" :pl="32" :pr="24">
      <BoxComponent :mb="24">
        <FlexComponent justify="space-between" align="center">
          <search-box :on-change="(value: string) => (searchQuery = value)" placeholder="Search" class="search" />
          <FlexComponent align="center" :gap="16">
            <CampaignFixedFee
              v-if="campaign.hasAdditionalPayment && advertiser?.currencyObj?.code"
              :fee="{ amount: campaignPayment, currency: advertiser.currencyObj.code }"
            />
            <router-link
              v-if="isMediaLinkVisible"
              :to="{ name: 'content-library', query: { campaignId: campaign.id, campaignName: campaign.title } }"
            >
              <el-button round>See all stories</el-button>
            </router-link>
            <ButtonComponent
              v-if="userHasPermissionsToEdit"
              id="manage-campaign-invite-section"
              variant="accent"
              @click="isInviteUsersModalOpen = true"
            >
              Invite Creators
            </ButtonComponent>
            <CampaignParticipantsSettings :campaign-id="route.params.id as string" />
          </FlexComponent>
        </FlexComponent>
      </BoxComponent>

      <AddUserDialog
        v-if="isInviteUsersModalOpen"
        v-model="isInviteUsersModalOpen"
        :user-tags="userTagsFiltered"
        :campaign-token="campaign.token"
        :campaign-id="campaign.id"
        :invitations-left="campaign.invitationsLeft"
        @on-close="
          () => {
            if (store.getters['onboarding/currentOnboardingStep'] === 3) {
              store.dispatch('onboarding/setCurrentCard', 'manage-campaign-finish')
              store.dispatch('onboarding/setOnboardingActivity', true)
            }
          }
        "
        @on-invite="onInvite"
      />

      <DialogModal v-if="'created' in route.query && campaign.token" :before-close="handleCloseSuccessCreationPopup">
        <TypographyComponent variant="headline-medium">Campaign created 🎉</TypographyComponent>
        <BoxComponent :mb="24" :mt="24">
          <TypographyComponent variant="body-large">
            Your campaign is live! Share the link to invite more creators via DM or email. Access it through the
            <TypographyComponent is="span" variant="body-large" :font-weight="600">Add user</TypographyComponent> link anytime.
          </TypographyComponent>
        </BoxComponent>
        <invite-user-link :campaign-token="campaign.token" />
      </DialogModal>

      <BoxComponent v-if="campaign" id="manage-campaign-finish" :mb="24">
        <!-- @vue-expect-error todo fix me -->
        <CampaignParticipantsColumns
          :campaign="campaign"
          :currency="advertiser?.currencyObj"
          :search-query="searchQuery"
          :should-update-first-column="shouldUpdateFirstColumn"
          @on-columns-update="
            (value: boolean) => {
              isMediaLinkVisible = value
              shouldUpdateFirstColumn = false
            }
          "
        />
      </BoxComponent>
    </BoxComponent>
  </div>
</template>

<style lang="scss" scoped>
.content {
  overflow: hidden;
}

.back-link {
  width: 24px;
  height: 24px;
  transform: rotate(180deg);
}

.search {
  width: 240px;
}

.sync-status__message {
  display: none;
}

.sync-status {
  position: fixed;
  bottom: 48px;
  right: 48px;
  border-radius: 120px;
  background: #fff;
  padding: 16px;
  box-shadow: 4px 4px 32px 0 rgba(0, 0, 0, 0.08);

  &:hover {
    .sync-status__message {
      display: block;
    }
  }
}
</style>
